export const ACTIVE_USER_NOTES_STORAGE_KEY = 'cms_active_user_notes_v1'

export interface ActiveUserNote {
	id: string
	userId: string
	content: string
	createdAt: string
	updatedAt: string
}

export type ActiveUserNotesStore = Record<string, ActiveUserNote[]>

const isValidNote = (value: unknown): value is ActiveUserNote => {
	if (!value || typeof value !== 'object') return false

	const note = value as ActiveUserNote
	return (
		typeof note.id === 'string' &&
		typeof note.userId === 'string' &&
		typeof note.content === 'string' &&
		typeof note.createdAt === 'string' &&
		typeof note.updatedAt === 'string'
	)
}

export const readActiveUserNotes = (): ActiveUserNotesStore => {
	if (typeof window === 'undefined') return {}

	try {
		const value = window.localStorage.getItem(ACTIVE_USER_NOTES_STORAGE_KEY)
		if (!value) return {}

		const parsed = JSON.parse(value)
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

		return Object.entries(parsed).reduce<ActiveUserNotesStore>(
			(result, [userId, notes]) => {
				if (!Array.isArray(notes)) return result

				const validNotes = notes.filter(
					(note) => isValidNote(note) && note.userId === userId,
				)
				if (validNotes.length > 0) {
					result[userId] = validNotes
				}
				return result
			},
			{},
		)
	} catch {
		return {}
	}
}

export const writeActiveUserNotes = (notes: ActiveUserNotesStore) => {
	if (typeof window === 'undefined') return

	try {
		window.localStorage.setItem(
			ACTIVE_USER_NOTES_STORAGE_KEY,
			JSON.stringify(notes),
		)
	} catch {
		// Keep the page usable when storage is unavailable or full.
	}
}

// UD-559: notes used to be kept in the browser only. These helpers let the page offer to move
// the ones still sitting in this browser to the server, and forget each one once it is saved.
// Oldest first, so the notes reach the server in the order they were written.
export const readActiveUserNotesFor = (userId: string): ActiveUserNote[] => {
	return [...(readActiveUserNotes()[userId] || [])].sort(
		(first, second) =>
			new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime(),
	)
}

export const removeActiveUserNote = (userId: string, noteId: string) => {
	const store = readActiveUserNotes()
	const remaining = (store[userId] || []).filter((note) => note.id !== noteId)
	if (remaining.length === (store[userId] || []).length) return
	const next = { ...store }
	if (remaining.length > 0) {
		next[userId] = remaining
	} else {
		delete next[userId]
	}
	writeActiveUserNotes(next)
}
